import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Switch } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { SettingsType } from '../../../../constants/enums'
import i18n from '../../../../utilities/i18n'
import { useSettingSelector } from '../../../Hooks/useSettings'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, { minWindowWidth } from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import Tooltip from '../../Tooltip'

const SwitchRoot = styled('div')({
    display: 'flex',
    flexFlow: 'column nowrap',
})
const SwitchTitle = styled('div')({
    fontWeight: 'bold',
    padding: getThemeSpacing(1),
})
const SwitchWrapper = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    fontSize: '0.785rem',
    padding: getThemeSpacing(1),
})
const SwitchContainer = styled(Switch)({
    flex: 'none',
    padding: getThemeSpacing(1),
    [`${Switch}.MuiSwitch-track`]: {
        borderRadius: 12,
    },
})
const SwitchWarning = styled('div')({
    color: palette.notificationRed,
    fontSize: '0.785rem',
    padding: getThemeSpacing(1),
})

const tooltipStyles = css({
    padding: getThemeSpacing(2),
    [minWindowWidth(APP_BREAKPOINTS.sm)]: {
        maxWidth: '30vw',
    },
})
const tooltipTitleStyles = css({
    padding: `0 ${getThemeSpacing(2)} ${getThemeSpacing(1)} 0`,
    fontWeight: 'bold',
})

const TruePrivateTooltip = memo(() => {
    // children of `Tooltip` must be a single memoized component
    const content = useMemo(
        () => (
            <>
                <div css={tooltipTitleStyles}>
                    {i18n.t('chat.truePrivateTooltipTitle')}
                </div>
                <div>{i18n.t('chat.truePrivateTooltipMessage')}</div>
            </>
        ),
        [],
    )
    return <Tooltip styleOverrides={tooltipStyles}>{content}</Tooltip>
})

interface TruePrivateSwitchProps {
    isTruePrivateShowEnabled?: boolean
}

export const TruePrivateSwitch = memo((props: TruePrivateSwitchProps) => {
    const [truePrivate, saveTruePrivateSetting] = useSettingSelector(
        SettingsType.MY_ACCOUNT,
        'truePrivate',
    )
    const { isTruePrivateShowEnabled } = props

    const onSwitchTruePrivate = useCallback(({ target: { checked } }) => {
        saveTruePrivateSetting(checked ? 1 : 0)
    }, [])

    return (
        <SwitchRoot>
            <SwitchTitle>
                {i18n.t('chat.confirmRequestPrivateShow')}
            </SwitchTitle>
            <SwitchWrapper>
                <div>{i18n.t('chat.truePrivate')}?</div>
                <TruePrivateTooltip />
                <SwitchContainer
                    checked={Boolean(truePrivate)}
                    color="primary"
                    data-testid={`Room__SwitchTruePrivate`}
                    onChange={onSwitchTruePrivate}
                />
            </SwitchWrapper>
            {isTruePrivateShowEnabled && (
                <SwitchWarning>{i18n.t('chat.truePrivateOnly')}</SwitchWarning>
            )}
        </SwitchRoot>
    )
})
